import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import AuthGate from './components/auth/AuthGate';
import AuthLayout from './layouts/AuthLayout';
import DashboardLayout from './layouts/DashboardLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Unauthorized from './pages/Unauthorized';

import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import DashboardHome from './pages/DashboardHome';

import { PERMISSIONS } from './config/permissions';
import AccessControl from './pages/rbac/AccessControl';
import UserManagement from './pages/rbac/UserManagement';
import HierarchyPage from './pages/rbac/HierarchyPage';
import ListingsPage from './pages/product-master/pre-ipo/ListingsPage';
import FakeInventoryPage from './pages/product-master/pre-ipo/FakeInventoryPage';
import RealInventoryPage from './pages/product-master/pre-ipo/RealInventoryPage';
import AttributeCatalogPage from './pages/product-master/pre-ipo/AttributeCatalogPage';
import ProductAttributeMappingPage from './pages/product-master/pre-ipo/ProductAttributeMappingPage';
import BasketManagementPage from './pages/basket/BasketManagementPage';
import SupplierMastersListPage from './pages/product-master/masters/SupplierMastersListPage';
import SupplierOnboardingPage from './pages/product-master/masters/SupplierOnboardingPage';
import SupplierProfilePage from './pages/product-master/masters/SupplierProfilePage';
import IFAListPage from './pages/ifa/IFAListPage';
import IFAOnboardingPage from './pages/ifa/IFAOnboardingPage';
import PartnerDetailsPage from './pages/ifa/PartnerDetailsPage';
import InvestorListPage from './pages/investors/InvestorListPage';
import InvestorOnboardingPage from './pages/investors/InvestorOnboardingPage';
import InvestorOnboardEntryPage from './pages/investors/InvestorOnboardEntryPage';
import InvestorProfilePage from './pages/investors/InvestorProfilePage';
import RAReferralsPage from './pages/investors/RAReferralsPage';
import DashboardPageWrapper from './components/layout/DashboardPageWrapper';
import apiClient from './api/apiClient';
import PricingGroupsPage from './pages/pricing/PricingGroupsPage';
import ProductSlabMatrixPage from './pages/pricing/ProductSlabMatrixPage';
import ProductPricingTiersPage from './pages/pricing/ProductPricingTiersPage';
import SpecialProductPricingTiersPage from './pages/pricing/SpecialProductPricingTiersPage';
import SpecialPricingRequestsPage from './pages/pricing/SpecialPricingRequestsPage';
import SharePriceChartPage from './pages/pricing/SharePriceChartPage';
import NewsListPage from './pages/news/NewsListPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import NewSellRequestPage from './pages/product-master/pre-ipo/NewSellRequestPage';
import NewShareRequestPage from './pages/product-master/pre-ipo/NewShareRequestPage';
import OrderManagementPage from './pages/product-master/pre-ipo/order-management/OrderManagementPage';
import PlaceSingleShareOrderPage from './pages/product-master/pre-ipo/order-management/PlaceSingleShareOrderPage';
import PreIpoReportingPage from './pages/product-master/pre-ipo/reporting/PreIpoReportingPage';
import BasketOrderManagementPage from './pages/product-master/pre-ipo/basket-order-management/BasketOrderManagementPage';
import PlaceBasketOrderPage from './pages/product-master/pre-ipo/basket-order-management/PlaceBasketOrderPage';
import PurchaseDealSheetListPage from './pages/product-master/pre-ipo/purchase-deal-sheet/PurchaseDealSheetListPage';
import PurchaseDealSheetCreatePage from './pages/product-master/pre-ipo/purchase-deal-sheet/PurchaseDealSheetCreatePage';
import PurchaseDealSheetDetailPage from './pages/product-master/pre-ipo/purchase-deal-sheet/PurchaseDealSheetDetailPage';
import TradeCommitmentsSummaryPage from './pages/product-master/pre-ipo/trade-commitments/TradeCommitmentsSummaryPage';
import TradeCommitmentsListPage from './pages/product-master/pre-ipo/trade-commitments/TradeCommitmentsListPage';
import TradeCommitmentCreatePage from './pages/product-master/pre-ipo/trade-commitments/TradeCommitmentCreatePage';
import TradeCommitmentDetailPage from './pages/product-master/pre-ipo/trade-commitments/TradeCommitmentDetailPage';
import WhatsAppIntegrationPage from './pages/settings/WhatsAppIntegrationPage';
import SpecialWhatsAppBroadcastPage from './pages/settings/SpecialWhatsAppBroadcastPage';
import WhatsAppGroupsPage from './pages/pricing/WhatsAppGroupsPage';
import WhatsAppChatbotContactsPage from './pages/pricing/WhatsAppChatbotContactsPage';
import PublishPage from './pages/pricing/PublishPage';
import BusinessTeamAlertsPage from './pages/pricing/BusinessTeamAlertsPage';
import SalesTargetsPage from './pages/sales-targets/SalesTargetsPage';
import SalesQuarterTargetsPage from './pages/sales-quarter-targets/SalesQuarterTargetsPage';
import MtdBusinessReportPage from './pages/mtd-business-report/MtdBusinessReportPage';
import SalesPerformanceReportPage from './pages/sales-performance-report/SalesPerformanceReportPage';
import YtdMisReportPage from './pages/ytd-mis-report/YtdMisReportPage';
import SalesMisReportPage from './pages/sales/SalesMisReportPage';
import OrderWiseRevenuePage from './pages/sales/OrderWiseRevenuePage';
import OfflineProductSalesListPage from './pages/sales/offline-product-sales/OfflineProductSalesListPage';
import BannersPage from './pages/config/BannersPage';
import TracxnUploadPage from './pages/config/TracxnUploadPage';

/**
 * Basic authentication route wrapper
 * Checks if user is authenticated, not permissions
 */
const AuthenticatedRoute = ({ children }) => {
  const isAuthenticated = apiClient.isAuthenticated();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

/** Redirect legacy /product-master/pre-ipo/* URLs to /pre-ipo/* */
function LegacyPreIpoPathRedirect() {
  const location = useLocation();
  const redirected = location.pathname.replace(/^\/product-master\/pre-ipo/, '/pre-ipo');
  return <Navigate to={`${redirected}${location.search}${location.hash}`} replace />;
}

/** Redirect legacy /product-master/masters/supplier-masters/* URLs to /supplier-masters/* */
function LegacySupplierMastersPathRedirect() {
  const location = useLocation();
  const redirected = location.pathname.replace(
    /^\/product-master\/masters\/supplier-masters/,
    '/supplier-masters'
  );
  return <Navigate to={`${redirected}${location.search}${location.hash}`} replace />;
}

/** Redirect legacy /supplier-masters/:id/profile URLs to /suppliers/:id/profile */
function LegacySupplierProfilePathRedirect() {
  const location = useLocation();
  const redirected = location.pathname.replace(
    /^\/supplier-masters\/([^/]+)\/profile$/,
    '/suppliers/$1/profile'
  );
  return <Navigate to={`${redirected}${location.search}${location.hash}`} replace />;
}

export default function Router() {
  return (
    <Routes>
      {/* "/" -> login for guests, dashboard for logged-in users */}
      <Route path="/" element={<AuthGate />} />

      {/* Auth layout: login & password reset (no account creation) */}
      <Route path="/login" element={<AuthLayout />}>
        <Route index element={<Login />} />
      </Route>
      <Route path="/forgot-password" element={<AuthLayout />}>
        <Route index element={<ForgotPassword />} />
      </Route>
      <Route path="/reset-password" element={<AuthLayout />}>
        <Route index element={<ResetPassword />} />
      </Route>
      <Route path="/setup-password" element={<AuthLayout />}>
        <Route index element={<ResetPassword />} />
      </Route>

      {/* Unauthorized page - accessible to authenticated users */}
      <Route
        path="/unauthorized"
        element={
          <AuthenticatedRoute>
            <Unauthorized />
          </AuthenticatedRoute>
        }
      />



      {/* ****************************************************************************************************************** */}
      {/* Protected dashboard routes with permission checks */}
      <Route
        element={
          <AuthenticatedRoute>
            <DashboardLayout />
          </AuthenticatedRoute>
        }
      >
        {/* Dashboard Home */}
        <Route
          path="dashboard"
          element={
            <ProtectedRoute permission={PERMISSIONS.DASHBOARD_VIEW}>
              <DashboardHome />
            </ProtectedRoute>
          }
        />

        <Route
          path="sales/targets"
          element={
            <ProtectedRoute permission={PERMISSIONS.SALES_RM_TARGETS_VIEW}>
              <SalesTargetsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales/quarter-targets"
          element={
            <ProtectedRoute permission={PERMISSIONS.SALES_RM_QUARTER_TARGETS_VIEW}>
              <SalesQuarterTargetsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales/mtd-business-report"
          element={
            <ProtectedRoute permission={PERMISSIONS.MTD_BUSINESS_REPORT_VIEW}>
              <MtdBusinessReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales/performance-report"
          element={
            <ProtectedRoute permission={PERMISSIONS.SALES_PERFORMANCE_REPORT_VIEW}>
              <SalesPerformanceReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales/ytd-mis-report"
          element={
            <ProtectedRoute permission={PERMISSIONS.YTD_MIS_REPORT_VIEW}>
              <YtdMisReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales/sales-mis-report"
          element={
            <ProtectedRoute superAdminOnly>
              <SalesMisReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales/order-wise-revenue"
          element={
            <ProtectedRoute permission={PERMISSIONS.ORDER_WISE_REVENUE_EXPORT}>
              <OrderWiseRevenuePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="sales/offline-sell-transactions"
          element={
            <ProtectedRoute permission={PERMISSIONS.OFFLINE_PRODUCT_SALES_VIEW}>
              <OfflineProductSalesListPage />
            </ProtectedRoute>
          }
        />

        {/* Investors Routes - Nested with different permissions */}
        <Route path="investors">
          {/* List page - requires LIST_VIEW permission */}
          <Route
            path="list"
            element={
              <ProtectedRoute permission={PERMISSIONS.INVESTORS_LIST_VIEW}>
                <InvestorListPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="ra-referrals"
            element={
              <ProtectedRoute permission={PERMISSIONS.RA_REFERRAL_VIEW}>
                <RAReferralsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="onboard"
            element={
              <ProtectedRoute permission={PERMISSIONS.INVESTORS_CREATE}>
                <InvestorOnboardEntryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="onboard/:sessionUuid"
            element={
              <ProtectedRoute permission={PERMISSIONS.INVESTORS_CREATE}>
                <InvestorOnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path=":id/profile"
            element={
              <ProtectedRoute permission={PERMISSIONS.INVESTORS_VIEW}>
                <InvestorProfilePage />
              </ProtectedRoute>
            }
          />

          {/* Create page - requires CREATE permission */}
          <Route
            path="create"
            element={
              <ProtectedRoute permission={PERMISSIONS.INVESTORS_CREATE}>
                <DashboardPageWrapper>
                  <h1 className="text-2xl font-bold">Create Investor</h1>
                  <p className="mt-2 text-gray-600">Requires: INVESTORS_CREATE permission</p>
                </DashboardPageWrapper>
              </ProtectedRoute>
            }
          />

          {/* Edit page - requires EDIT permission */}
          <Route
            path=":id/edit"
            element={
              <ProtectedRoute permission={PERMISSIONS.INVESTORS_EDIT}>
                <DashboardPageWrapper>
                  <h1 className="text-2xl font-bold">Edit Investor</h1>
                  <p className="mt-2 text-gray-600">Requires: INVESTORS_EDIT permission</p>
                </DashboardPageWrapper>
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Product Master Routes */}
        <Route
          path="product-master/fixed-deposit"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_FIXED_DEPOSIT_VIEW}>
              <DashboardPageWrapper>
                <h1 className="text-2xl font-bold">Fixed Deposit</h1>
                <p className="mt-2 text-gray-600">This page is protected by RBAC permissions.</p>
              </DashboardPageWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path="pre-ipo/reporting"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_REPORTING_VIEW}>
              <PreIpoReportingPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="pre-ipo/real-inventory"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_REAL_INVENTORY_VIEW}>
              <RealInventoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pre-ipo/new-shares-request"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_NEW_SHARES_REQUEST_VIEW}>
              <NewShareRequestPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pre-ipo/new-sell-request"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_NEW_SELL_REQUEST_VIEW}>
              <NewSellRequestPage />
            </ProtectedRoute>
          }
        />

        {/* Order Management (Pre-IPO) */}
        <Route
          path="pre-ipo/order-management"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_ORDER_MANAGEMENT_VIEW}>
              <OrderManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pre-ipo/order-management/place"
          element={
            <ProtectedRoute permission={PERMISSIONS.INVESTORS_PLACE_SELL_ORDER}>
              <PlaceSingleShareOrderPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="pre-ipo/basket-order-management"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_ORDER_MANAGEMENT_VIEW}>
              <BasketOrderManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pre-ipo/basket-order-management/place"
          element={
            <ProtectedRoute permission={PERMISSIONS.INVESTORS_PLACE_SELL_ORDER}>
              <PlaceBasketOrderPage />
            </ProtectedRoute>
          }
        />

        {/* Nested routes example: Inventory with view vs edit permissions */}
        <Route path="pre-ipo/inventory">
          {/* List page - requires VIEW permission */}
          <Route
            index
            element={
              <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_INVENTORY_VIEW}>
                <FakeInventoryPage />
              </ProtectedRoute>
            }
          />

          {/* Update page - requires EDIT permission */}
          <Route
            path=":id/update"
            element={
              <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_INVENTORY_EDIT}>
                <DashboardPageWrapper>
                  <h1 className="text-2xl font-bold">Update Inventory Item</h1>
                  <p className="mt-2 text-gray-600">Requires: INVENTORY_EDIT permission</p>
                  <p className="mt-1 text-sm text-gray-500">User with only VIEW permission cannot access this.</p>
                </DashboardPageWrapper>
              </ProtectedRoute>
            }
          />

          {/* Create page - requires EDIT permission */}
          <Route
            path="create"
            element={
              <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_INVENTORY_EDIT}>
                <DashboardPageWrapper>
                  <h1 className="text-2xl font-bold">Create Inventory Item</h1>
                  <p className="mt-2 text-gray-600">Requires: INVENTORY_EDIT permission</p>
                </DashboardPageWrapper>
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Listings page - requires PRODUCTS_VIEW permission */}
        <Route
          path="pre-ipo/listings"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCTS_VIEW}>
              <ListingsPage />
            </ProtectedRoute>
          }
        />
        {/* Product attribute mapping - same permission as listings */}
        <Route
          path="pre-ipo/listings/:productId/attributes"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCTS_VIEW}>
              <ProductAttributeMappingPage />
            </ProtectedRoute>
          }
        />

        {/* Attribute Catalog - PRODUCTS_VIEW (GET); create/edit/delete gated in page */}
        <Route
          path="pre-ipo/attribute-catalog"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCTS_VIEW}>
              <AttributeCatalogPage />
            </ProtectedRoute>
          }
        />

        {/* Basket Management (under Pre-IPO) */}
        <Route
          path="pre-ipo/basket-management"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_BASKET_LISTINGS_VIEW}>
              <BasketManagementPage />
            </ProtectedRoute>
          }
        />

        {/* Purchase Deal Sheet */}
        <Route
          path="pre-ipo/purchase-deal-sheet"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_PURCHASE_DEAL_SHEET_VIEW}>
              <PurchaseDealSheetListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pre-ipo/purchase-deal-sheet/create"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_PURCHASE_DEAL_SHEET_MANAGE}>
              <PurchaseDealSheetCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pre-ipo/purchase-deal-sheet/:id"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_PURCHASE_DEAL_SHEET_VIEW}>
              <PurchaseDealSheetDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="pre-ipo/trade-commitments/summary"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_TRADE_COMMITMENT_VIEW}>
              <TradeCommitmentsSummaryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pre-ipo/trade-commitments"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_TRADE_COMMITMENT_VIEW}>
              <TradeCommitmentsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pre-ipo/trade-commitments/create"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_TRADE_COMMITMENT_MANAGE}>
              <TradeCommitmentCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pre-ipo/trade-commitments/:id"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRODUCT_PRE_IPO_TRADE_COMMITMENT_VIEW}>
              <TradeCommitmentDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Legacy Pre-IPO URLs → /pre-ipo/* */}
        <Route path="product-master/pre-ipo/*" element={<LegacyPreIpoPathRedirect />} />

        {/* Supplier Masters */}
        <Route
          path="supplier-masters"
          element={
            <ProtectedRoute permission={PERMISSIONS.SUPPLIERS_VIEW}>
              <SupplierMastersListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="supplier-masters/onboard"
          element={
            <ProtectedRoute permission={PERMISSIONS.SUPPLIERS_MANAGE}>
              <SupplierOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="supplier-masters/onboard/:supplierId"
          element={
            <ProtectedRoute permission={PERMISSIONS.SUPPLIERS_MANAGE}>
              <SupplierOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="suppliers/:id/profile"
          element={
            <ProtectedRoute permission={PERMISSIONS.SUPPLIERS_VIEW}>
              <SupplierProfilePage />
            </ProtectedRoute>
          }
        />
        <Route path="supplier-masters/:id/profile" element={<LegacySupplierProfilePathRedirect />} />

        {/* Legacy supplier URLs → /supplier-masters/* */}
        <Route path="product-master/masters/supplier-masters/*" element={<LegacySupplierMastersPathRedirect />} />

        {/* IFA Routes */}
        <Route
          path="partners/list"
          element={
            <ProtectedRoute permission={PERMISSIONS.PARTNER_LIST_VIEW}>
              <IFAListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="partners/onboard"
          element={
            <ProtectedRoute permission={PERMISSIONS.PARTNER_CREATE}>
              <IFAOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="partners/onboard/:partnerId"
          element={
            <ProtectedRoute permission={PERMISSIONS.PARTNER_CREATE}>
              <IFAOnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="partner/details/:partner_id"
          element={
            <ProtectedRoute permission={PERMISSIONS.PARTNER_LIST_VIEW}>
              <PartnerDetailsPage />
            </ProtectedRoute>
          }
        />

        {/* Pricing Routes */}
        <Route
          path="pricing/groups"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRICING_GROUPS_VIEW}>
              <PricingGroupsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pricing/whatsapp-groups"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRICING_WHATSAPP_GROUPS_VIEW}>
              <WhatsAppGroupsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pricing/whatsapp-chatbot-contacts"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRICING_WHATSAPP_CHATBOT_VIEW}>
              <WhatsAppChatbotContactsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pricing/product-slab-matrix"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRICING_SLAB_MATRIX_VIEW}>
              <ProductSlabMatrixPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pricing/special-tiers"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRICING_SPECIAL_TIERS_VIEW}>
              <ProductPricingTiersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pricing/special-product-tiers"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRICING_SPECIAL_PRODUCT_TIERS_VIEW}>
              <SpecialProductPricingTiersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pricing/special-pricing-requests"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRICING_SPECIAL_PRICING_REQUESTS_VIEW}>
              <SpecialPricingRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pricing/publish"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRICING_PUBLISH_VIEW}>
              <PublishPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="pricing/business-team-alerts"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRICING_BUSINESS_ALERTS_VIEW}>
              <BusinessTeamAlertsPage />
            </ProtectedRoute>
          }
        />

        {/* Share Price Chart */}
        <Route
          path="price-chart"
          element={
            <ProtectedRoute permission={PERMISSIONS.PRICE_CHART_VIEW}>
              <SharePriceChartPage />
            </ProtectedRoute>
          }
        />

        {/* News */}
        <Route
          path="news"
          element={
            <ProtectedRoute permission={PERMISSIONS.NEWS_VIEW}>
              <NewsListPage />
            </ProtectedRoute>
          }
        />

        {/* Notifications */}
        <Route
          path="notifications"
          element={
            <ProtectedRoute permission={PERMISSIONS.ADMIN_NOTIFICATIONS_VIEW}>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />

        {/* Commissions */}
        <Route
          path="commissions"
          element={
            <ProtectedRoute permission={PERMISSIONS.COMMISSIONS_VIEW}>
              <DashboardPageWrapper>
                <h1 className="text-2xl font-bold">Commissions</h1>
                <p className="mt-2 text-gray-600">This page is protected by RBAC permissions.</p>
              </DashboardPageWrapper>
            </ProtectedRoute>
          }
        />

        {/* Introducer */}
        <Route
          path="introducer"
          element={
            <ProtectedRoute permission={PERMISSIONS.INTRODUCER_VIEW}>
              <DashboardPageWrapper>
                <h1 className="text-2xl font-bold">Introducer</h1>
                <p className="mt-2 text-gray-600">This page is protected by RBAC permissions.</p>
              </DashboardPageWrapper>
            </ProtectedRoute>
          }
        />

        {/* RBAC Routes */}
        <Route
          path="rbac/user-management"
          element={
            <ProtectedRoute permission={PERMISSIONS.RBAC_USER_MANAGEMENT_VIEW}>
              <UserManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="rbac/roles-permissions"
          element={
            <ProtectedRoute superAdminOnly>
              <AccessControl />
            </ProtectedRoute>
          }
        />

        <Route
          path="rbac/reset-password"
          element={
            <ProtectedRoute permission={PERMISSIONS.RBAC_RESET_PASSWORD_VIEW}>
              <DashboardPageWrapper>
                <h1 className="text-2xl font-bold">Reset Password</h1>
                <p className="mt-2 text-gray-600">This page is protected by RBAC permissions.</p>
              </DashboardPageWrapper>
            </ProtectedRoute>
          }
        />

        <Route
          path="rbac/hierarchy"
          element={
            <ProtectedRoute permission={PERMISSIONS.RBAC_HIERARCHY_VIEW}>
              <HierarchyPage />
            </ProtectedRoute>
          }
        />

        {/* Configuration Routes */}
        <Route
          path="config/banners"
          element={
            <ProtectedRoute permission={PERMISSIONS.BANNERS_VIEW}>
              <BannersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="config/tracxn-upload"
          element={
            <ProtectedRoute permissions={[PERMISSIONS.PRODUCTS_EDIT, PERMISSIONS.PRODUCTS_CREATE]}>
              <TracxnUploadPage />
            </ProtectedRoute>
          }
        />

        {/* Settings → WhatsApp Integration (Whapi Cloud) */}
        <Route
          path="settings/whatsapp"
          element={
            <ProtectedRoute permission={PERMISSIONS.INTEGRATION_WHATSAPP_VIEW}>
              <WhatsAppIntegrationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/special-whatsapp-broadcast"
          element={
            <ProtectedRoute superAdminOnly>
              <SpecialWhatsAppBroadcastPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Fallback: send unknown routes to home (login or dashboard via AuthGate) */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
